/**
 * Process Flow Card
 * 
 * Displays processes grouped by process_type with artifact flow visualization.
 * Shows how artifacts (PROCESSITEM) move through processes via state transitions.
 * 
 * Architect View: Graphical process sequence with colored artifact flows
 */

import React, { useEffect, useState } from 'react';
import api from '../../services/api/api';
import './ProcessFlowCard.css';

interface ProcessFlowCardProps {
  metadata: {
    processTypes: string[];  // e.g., ["DEMAND", "SUPPLY", "DELIVERY"]
    artifact_tree_id?: string;
    viewMode?: string;  // "architect", "operator", etc.
    showArtifactFlow?: boolean;
    showTransitions?: boolean;
  };
  threadId?: string;
  onContextSelect?: (type: 'process' | 'artifact' | 'transition', id: string, data: any) => void;
}

interface Process {
  id: string;
  name: string;
  description?: string;
  artifact_ref: string;
  process_type: string;  // DEMAND, SUPPLY, DELIVERY, etc.
  process_status: string;
  duration?: number;
  conversion_rate?: number;
}

interface Artifact {
  id: string;
  name: string;
  description?: string;
  artifact_type_id: string;
}

interface ArtifactFlow {
  artifact_id: string;
  process_id: string;
  transition_id: string;
  from_state: { id: string; name: string; state_type: string };
  to_state: { id: string; name: string; state_type: string };
  transition_type: string;
}

interface ProcessFlowData {
  process_type: string;
  processes: Process[];
  artifacts: Artifact[];
  transitions: any[];
  artifact_flow: ArtifactFlow[];
}

interface CombinedProcessFlowData {
  ordered_processes: Process[];
  processes_by_type: Record<string, Process[]>;
  artifacts: Artifact[];
  artifact_flow_paths: ArtifactFlowPath[];
  transitions: any[];
}

interface ArtifactFlowPath {
  artifact_id: string;
  artifact_name: string;
  process_sequence: {
    process_id: string;
    process_name: string;
    process_type: string;
  }[];
  transitions: ArtifactFlow[];
}

// Color palette for artifacts
const ARTIFACT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
];

export const ProcessFlowCard: React.FC<ProcessFlowCardProps> = ({
  metadata,
  threadId,
  onContextSelect
}) => {
  const { processTypes, artifact_tree_id, showArtifactFlow = true } = metadata;
  const [combinedFlow, setCombinedFlow] = useState<CombinedProcessFlowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [artifactColors, setArtifactColors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadProcessFlows();
  }, [processTypes, artifact_tree_id]);

  const loadProcessFlows = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if flow data is provided directly (for demo mode)
      if ((metadata as any).flowData) {
        console.log('🎯 [ProcessFlowCard] Using provided flow data (demo mode)');
        const flowData: CombinedProcessFlowData = (metadata as any).flowData;
        setCombinedFlow(flowData);

        // Assign colors to unique artifacts
        const colors: Record<string, string> = {};
        flowData.artifacts.forEach((artifact, index) => {
          colors[artifact.id] = ARTIFACT_COLORS[index % ARTIFACT_COLORS.length];
        });
        setArtifactColors(colors);
        setLoading(false);
        return;
      }

      // Otherwise, fetch from API (for company instance mode)
      console.log('🌐 [ProcessFlowCard] Fetching flow data from API');
      
      // Ensure processTypes is an array
      const typesArray = Array.isArray(processTypes) ? processTypes : [];
      
      if (typesArray.length === 0) {
        console.warn('ProcessFlowCard: No process types provided');
        setError('No process types specified');
        setLoading(false);
        return;
      }

      // Use the new combined endpoint
      // FastAPI expects array params as separate query params: ?process_types=DEMAND&process_types=SUPPLY
      const processTypesParams = typesArray.map(pt => `process_types=${pt}`).join('&');
      const treeParam = artifact_tree_id ? `&artifact_tree_id=${artifact_tree_id}` : '';
      const response = await api.get(`/process-flow/combined?${processTypesParams}${treeParam}`);
      
      const flowData: CombinedProcessFlowData = response.data;
      setCombinedFlow(flowData);

      // Assign colors to unique artifacts
      const colors: Record<string, string> = {};
      flowData.artifacts.forEach((artifact, index) => {
        colors[artifact.id] = ARTIFACT_COLORS[index % ARTIFACT_COLORS.length];
      });
      setArtifactColors(colors);

    } catch (err: any) {
      console.error('Failed to load process flows:', err);
      setError(err.message || 'Failed to load process flow data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessClick = (process: Process) => {
    if (onContextSelect) {
      onContextSelect('process', process.id, process);
    }
  };

  const handleArtifactClick = (artifact: Artifact) => {
    if (onContextSelect) {
      onContextSelect('artifact', artifact.id, artifact);
    }
  };

  const handleTransitionClick = (flow: ArtifactFlow) => {
    if (onContextSelect) {
      onContextSelect('transition', flow.transition_id, flow);
    }
  };

  if (loading) {
    return (
      <div className="process-flow-card loading">
        <div className="loading-spinner">Loading process flows...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="process-flow-card error">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  if (!combinedFlow) {
    return (
      <div className="process-flow-card">
        <div className="no-data">No process flow data available</div>
      </div>
    );
  }

  // Helper to get process type class name
  const getProcessTypeClass = (processType: string): string => {
    return processType.toLowerCase();
  };

  // Helper to determine which artifacts flow through each process
  const getArtifactsForProcess = (processId: string): string[] => {
    const artifactIds: string[] = [];
    combinedFlow.artifact_flow_paths.forEach((flowPath) => {
      const hasProcess = flowPath.process_sequence.some(p => p.process_id === processId);
      if (hasProcess) {
        artifactIds.push(flowPath.artifact_id);
      }
    });
    return artifactIds;
  };

  // Build dependency graph: processes arranged in grid (columns = stage, rows = flow)
  const buildDependencyGraph = () => {
    const processPositions = new Map<string, number>(); // process_id -> column position
    const processFlows = new Map<string, Process[]>(); // flow_id -> processes in order
    
    // Track which processes have been placed
    const placed = new Set<string>();
    
    // Helper: get max position of processes that feed into this one
    const getMaxInputPosition = (processId: string): number => {
      let maxPos = -1;
      
      combinedFlow.artifact_flow_paths.forEach((flowPath) => {
        const processIndex = flowPath.process_sequence.findIndex(p => p.process_id === processId);
        if (processIndex > 0) {
          // Get previous process in this flow path
          const prevProcess = flowPath.process_sequence[processIndex - 1];
          const prevPos = processPositions.get(prevProcess.process_id);
          if (prevPos !== undefined && prevPos > maxPos) {
            maxPos = prevPos;
          }
        }
      });
      
      return maxPos;
    };
    
    // Assign column positions to all processes
    combinedFlow.artifact_flow_paths.forEach((flowPath) => {
      flowPath.process_sequence.forEach((procInSeq, seqIndex) => {
        const processId = procInSeq.process_id;
        
        if (!placed.has(processId)) {
          // Determine column for this process
          const maxInputPos = getMaxInputPosition(processId);
          const column = maxInputPos + 1;
          
          processPositions.set(processId, column);
          placed.add(processId);
        }
      });
    });
    
    // Build grid: Map of column -> Map of flow -> Process
    const grid = new Map<number, Map<string, Process[]>>();
    
    combinedFlow.artifact_flow_paths.forEach((flowPath) => {
      const flowId = flowPath.artifact_id;
      
      flowPath.process_sequence.forEach((procInSeq) => {
        const processId = procInSeq.process_id;
        const column = processPositions.get(processId)!;
        const process = combinedFlow.ordered_processes.find(p => p.id === processId);
        
        if (process) {
          if (!grid.has(column)) {
            grid.set(column, new Map());
          }
          
          const columnMap = grid.get(column)!;
          if (!columnMap.has(flowId)) {
            columnMap.set(flowId, []);
          }
          
          columnMap.get(flowId)!.push(process);
        }
      });
    });
    
    // Convert grid to array format for rendering
    const sortedColumns = Array.from(grid.keys()).sort((a, b) => a - b);
    const layoutGrid: Array<Array<Process | null>> = [];
    
    // Get all unique flow IDs
    const allFlowIds = Array.from(new Set(combinedFlow.artifact_flow_paths.map(fp => fp.artifact_id)));
    
    // Build rows (one per flow)
    allFlowIds.forEach((flowId) => {
      const row: Array<Process | null> = [];
      
      sortedColumns.forEach((col) => {
        const columnMap = grid.get(col);
        const processesInCell = columnMap?.get(flowId) || [];
        
        // For now, take first process in cell (we'll handle multiple later)
        row.push(processesInCell[0] || null);
      });
      
      layoutGrid.push(row);
    });
    
    return { layoutGrid, processPositions, columnCount: sortedColumns.length, flowIds: allFlowIds };
  };

  const { layoutGrid, processPositions, columnCount, flowIds } = buildDependencyGraph();

  // Helper to find connections between processes
  const getProcessConnections = (fromProcessId: string): Array<{toProcessId: string, artifactId: string}> => {
    const connections: Array<{toProcessId: string, artifactId: string}> = [];
    
    combinedFlow.artifact_flow_paths.forEach((flowPath) => {
      const fromIndex = flowPath.process_sequence.findIndex(p => p.process_id === fromProcessId);
      if (fromIndex >= 0 && fromIndex < flowPath.process_sequence.length - 1) {
        const toProcess = flowPath.process_sequence[fromIndex + 1];
        connections.push({
          toProcessId: toProcess.process_id,
          artifactId: flowPath.artifact_id
        });
      }
    });
    
    return connections;
  };

  return (
    <div className="process-flow-card">
      <div className="process-flow-header">
        <h3>Business Flow Architecture</h3>
        <div className="view-mode-badge">{metadata.viewMode || 'architect'} view</div>
      </div>

      {/* Artifact Legend */}
      {showArtifactFlow && combinedFlow.artifacts.length > 0 && (
        <div className="artifact-legend">
          <h4>Artifact Flows</h4>
          <div className="artifact-list">
            {combinedFlow.artifacts.map((artifact) => (
              <div
                key={artifact.id}
                className="artifact-legend-item"
                onClick={() => handleArtifactClick(artifact)}
                style={{ borderColor: artifactColors[artifact.id] }}
              >
                <div
                  className="artifact-color-dot"
                  style={{ backgroundColor: artifactColors[artifact.id] }}
                />
                <span className="artifact-name">{artifact.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Process Flow Diagram - Grid Layout (Rows = Flows, Columns = Stages) */}
      <div className="process-diagram">
        <div className="process-dependency-grid">
          {layoutGrid.map((row, rowIndex) => {
            const flowId = flowIds[rowIndex];
            const flowPath = combinedFlow.artifact_flow_paths.find(fp => fp.artifact_id === flowId);
            
            return (
              <div key={flowId} className="process-grid-row">
                {row.map((cellProcess, colIndex) => {
                  if (!cellProcess) {
                    // Empty cell - maintains column alignment
                    return <div key={`${rowIndex}-${colIndex}`} className="process-grid-cell empty" />;
                  }
                  
                  const connections = getProcessConnections(cellProcess.id);
                  
                  return (
                    <div key={`${rowIndex}-${colIndex}`} className="process-grid-cell">
                      <div 
                        className="process-circle-node"
                        onClick={() => handleProcessClick(cellProcess)}
                      >
                        <div className={`process-circle ${getProcessTypeClass(cellProcess.process_type)}`}>
                          <div className="process-circle-type">
                            {cellProcess.process_type}
                          </div>
                        </div>
                        <div className="process-circle-name">
                          {cellProcess.name}
                        </div>
                        {(cellProcess.duration || cellProcess.conversion_rate) && (
                          <div className="process-circle-metrics">
                            {cellProcess.duration && <span>{cellProcess.duration}d</span>}
                            {cellProcess.conversion_rate && (
                              <span>{(cellProcess.conversion_rate * 100).toFixed(0)}% conv.</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Arrows to next processes */}
                      {connections.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          right: '-80px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          zIndex: 10
                        }}>
                          {connections.map((conn, idx) => (
                            <div
                              key={`${cellProcess.id}-${conn.toProcessId}-${idx}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: artifactColors[conn.artifactId]
                              }}
                            >
                              <div style={{
                                width: '60px',
                                height: '3px',
                                background: 'currentColor'
                              }} />
                              <div style={{
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid currentColor',
                                borderTop: '5px solid transparent',
                                borderBottom: '5px solid transparent'
                              }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Flow Path Details */}
      {showArtifactFlow && combinedFlow.artifact_flow_paths.length > 0 && (
        <div className="artifact-flow-labels">
          <div className="artifact-flow-label-title">Complete Flow Paths:</div>
          {combinedFlow.artifact_flow_paths.map((flowPath) => (
            <div 
              key={flowPath.artifact_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: '#6b7280'
              }}
            >
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: artifactColors[flowPath.artifact_id],
                  flexShrink: 0
                }}
              />
              <strong style={{ color: '#374151', minWidth: '80px' }}>
                {flowPath.artifact_name}:
              </strong>
              <span>
                {flowPath.process_sequence.map(p => p.process_name).join(' → ')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className="card-help">
        <strong>💡 Reading the diagram:</strong> Processes are shown as circles (color-coded by type). 
        Colored arrows show which artifacts flow through each process. 
        <strong> Customer Receipt</strong> appears in both Customer and Product flows - 
        that's where demand meets supply!
      </div>
    </div>
  );
};

export default ProcessFlowCard;

